import { useState, useCallback, useMemo } from 'react';
import DataGrid, { Column, Selection, Scrolling, Paging, Pager, SearchPanel, Toolbar, Item } from 'devextreme-react/data-grid';
import { TextBox } from 'devextreme-react/text-box';
import { DateRangeBox } from 'devextreme-react/date-range-box';
import { Button } from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react/tooltip';
import LoadIndicator from 'devextreme-react/load-indicator';
import { useApiAnalyzer } from '../../../../api/api.analyzers';
import { HostQueryResult, SysI_Analizadores } from '../../../../types/types.analizadores';
import newToast from '../../../utils/new-toast/NewToast';
import moment from 'moment';
import './HostQueryPanel.scss';

interface Props {
  analyzer: SysI_Analizadores;
}

const HostQueryPanel: React.FC<Props> = ({ analyzer }) => {
  const { getHostQuery, SendDataToHostQuery } = useApiAnalyzer();

  const [orden, setOrden] = useState<string>('');
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [data, setData] = useState<HostQueryResult[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // Crear un key único para cada fila combinando Ord_Id y Raa_Sufijo
  const getRowKey = useCallback((item: HostQueryResult): string => {
    return `${item.Ord_Id}_${item.Raa_Sufijo ?? ''}`;
  }, []);

  // Transformar los datos para tener la clave única
  const dataWithKeys = useMemo(() => {
    return data.map((item) => ({
      ...item,
      _rowKey: getRowKey(item),
      _ordenDisplay: item.Raa_Sufijo ? `${item.Ord_Numero}.${item.Raa_Sufijo}` : item.Ord_Numero,
    }));
  }, [data, getRowKey]);

  const handleSearch = useCallback(async () => {
    const hasOrden = orden.trim() !== '';
    const hasDates = dateRange[0] !== null && dateRange[1] !== null;

    if (!hasOrden && !hasDates) {
      newToast('WARNING', 'Debe ingresar una orden o un rango de fechas', 3);
      return;
    }

    setLoading(true);
    setData([]);
    setSelectedRowKeys([]);

    try {
      const inicio = hasDates ? moment(dateRange[0]).format('YYYY-MM-DD') : null;
      const fin = hasDates ? moment(dateRange[1]).format('YYYY-MM-DD') : null;
      const ordenParam = hasOrden ? orden.trim() : null;

      const result = await getHostQuery(analyzer.Az_Id, inicio, fin, ordenParam);

      if (result && Array.isArray(result)) {
        setData(result);
        if (result.length === 0) {
          newToast('INFO', 'No se encontraron resultados', 3);
        }
      } else {
        setData([]);
        newToast('INFO', 'No se encontraron resultados', 3);
      }
    } catch (error) {
      console.error('Error al buscar datos:', error);
      newToast('ERROR', 'Error al buscar datos', 3);
    } finally {
      setLoading(false);
    }
  }, [analyzer.Az_Id, orden, dateRange, getHostQuery]);

  const handleSend = useCallback(async () => {
    if (selectedRowKeys.length === 0) {
      newToast('WARNING', 'Debe seleccionar al menos una fila', 3);
      return;
    }

    setSending(true);

    try {
      // Construir el diccionario de datos
      // Clave: "OrdId_Sufijo" o "OrdId" si no tiene sufijo
      // Valor: sufijo o null si no tiene
      const dataDict: { [key: string]: string | null } = {};

      selectedRowKeys.forEach((rowKey) => {
        const [ordIdStr, sufijo] = rowKey.split('_');
        if (sufijo) {
          dataDict[`${ordIdStr}_${sufijo}`] = sufijo;
        } else {
          dataDict[ordIdStr] = null;
        }
      });

      const payload = {
        IdAnalyzer: analyzer.Az_Id,
        Data: dataDict,
      };

      const result = await SendDataToHostQuery(payload);

      if (result) {
        newToast('SUCCESS', 'Datos enviados correctamente', 3);
        setSelectedRowKeys([]);
        // Refrescar los datos después de enviar
        handleSearch();
      } else {
        newToast('ERROR', 'Error al enviar los datos', 3);
      }
    } catch (error) {
      console.error('Error al enviar datos:', error);
      newToast('ERROR', 'Error al enviar los datos', 3);
    } finally {
      setSending(false);
    }
  }, [selectedRowKeys, analyzer.Az_Id, SendDataToHostQuery, handleSearch]);

  const handleSelectionChanged = useCallback((e: any) => {
    setSelectedRowKeys(e.selectedRowKeys || []);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return moment(dateString).format('DD/MM/YYYY');
    } catch {
      return dateString;
    }
  }, []);

  const renderDateCell = useCallback(
    (data: any) => {
      return <span>{formatDate(data.value)}</span>;
    },
    [formatDate]
  );

  const renderPatientCell = useCallback((data: any) => {
    const { Pc_Nombre, Pc_Apellido1, Pc_Apellido2 } = data.data;
    const fullName = [Pc_Nombre, Pc_Apellido1, Pc_Apellido2].filter(Boolean).join(' ');
    return (
      <div className='patient-cell'>
        <div className='patient-name'>{fullName}</div>
        <div className='patient-id'>{data.data.Pc_Identificacion}</div>
      </div>
    );
  }, []);

  return (
    <div className='host-query-panel'>
      <div className='host-query-filters'>
        <div className='filter-row'>
          <div className='filter-item'>
            <label>Orden</label>
            <TextBox value={orden} onValueChanged={(e) => setOrden(e.value || '')} placeholder='Número de orden' showClearButton width='100%' />
          </div>
          <div className='filter-item filter-item-dates'>
            <label>Rango de Fechas</label>
            <DateRangeBox value={dateRange} onValueChanged={(e) => setDateRange(e.value || [null, null])} displayFormat='dd/MM/yyyy' showClearButton width='100%' startDatePlaceholder='Fecha inicio' endDatePlaceholder='Fecha fin' />
          </div>
          <div className='filter-item filter-item-buttons'>
            <label>Acciones</label>
            <div className='action-buttons'>
              <Button id='hqSearchBtn' icon='search' type='default' stylingMode='contained' onClick={handleSearch} disabled={loading || sending} />
              <Tooltip target='#hqSearchBtn' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                Buscar
              </Tooltip>

              <div className='send-button-wrap'>
                <Button id='hqSendBtn' icon={sending ? undefined : 'send'} type='default' stylingMode='contained' onClick={handleSend} disabled={selectedRowKeys.length === 0 || sending}>
                  {sending && <LoadIndicator visible={true} width={18} height={18} />}
                </Button>
                {selectedRowKeys.length > 0 && !sending && <span className='send-badge'>{selectedRowKeys.length}</span>}
                <Tooltip target='#hqSendBtn' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                  {selectedRowKeys.length > 0 ? `Enviar ${selectedRowKeys.length} orden(es) al analizador` : 'Enviar al analizador'}
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='host-query-content'>
        {loading ? (
          <div className='loading-container'>
            <LoadIndicator visible={true} />
            <span>Buscando datos...</span>
          </div>
        ) : (
          <>
            <DataGrid dataSource={dataWithKeys} keyExpr='_rowKey' showBorders={false} showRowLines={true} showColumnLines={false} rowAlternationEnabled={true} hoverStateEnabled={true} height='100%' columnAutoWidth={true} wordWrapEnabled={true} selectedRowKeys={selectedRowKeys} onSelectionChanged={handleSelectionChanged}>
              <Selection mode='multiple' showCheckBoxesMode='always' />
              <SearchPanel visible={true} placeholder='Buscar...' />
              <Toolbar>
                <Item location='before'>
                  <div className='grid-selection-info'>
                    {selectedRowKeys.length > 0 ? (
                      <span>{selectedRowKeys.length} fila(s) seleccionada(s)</span>
                    ) : (
                      <span className='selection-hint'>Seleccione las órdenes que desea enviar al analizador</span>
                    )}
                  </div>
                </Item>
                <Item name='searchPanel' location='after' />
              </Toolbar>
              <Scrolling mode='standard' />
              <Paging enabled={true} defaultPageSize={50} />
              <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />

              <Column dataField='_ordenDisplay' caption='Orden' width={120} allowSorting={true} />

              <Column dataField='Pc_Identificacion' caption='Paciente' cellRender={renderPatientCell} allowSorting={true} />

              <Column dataField='Res_Fecha' caption='Fecha' width={150} cellRender={renderDateCell} allowSorting={true} />
            </DataGrid>
          </>
        )}
      </div>
    </div>
  );
};

export default HostQueryPanel;
